/**
 * THE RULES THE ENQUIRIES LIST IS READ BY, none of which needs a browser to be true: the tone of a
 * state, the moves a row offers, the server's order named in groups, who has it as a filter, and
 * the orders somebody chooses. And the one shape of an email address both typing forms check.
 */
import { ENQUIRY_STATES, HELD_BACK_LABELS, type EnquiryState } from '@fk/shared';
import type { EnquiryPatch, EnquiryRow } from '../../api.ts';
import type { RowGroup } from '../../components/ListView.tsx';
import { CHANNEL_LABELS } from '../../labels.ts';

export type Movable = NonNullable<EnquiryPatch['state']>;

export const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** What held a quote back, as the card and the panel say it (ops #86): the word, then their words. */
export function heldBackWords(row: Pick<EnquiryRow, 'heldBack' | 'heldBackNote'>): string | null {
  const word = row.heldBack ? HELD_BACK_LABELS[row.heldBack] : null;
  const words = row.heldBackNote ?? null;
  if (word && words) return `${word}: ${words}`;
  return word ?? words;
}

export function stateTone(state: EnquiryState): 'quiet' | 'gold' | 'sage' | 'coral' {
  switch (state) {
    case 'new':
      return 'gold';
    case 'booked':
      return 'sage';
    default:
      return 'quiet';
  }
}

/**
 * Where a row can go from here as a plain change of state. `booked` is never offered, because
 * booking is converting. `lost` has its own button, because it needs a reason. And `new` is not
 * offered either: the server would allow it, but nobody un-replies, and a state that is not worth
 * drawing is left out. The server checks all of this again and its sentence is what is shown.
 */
export function targetsFrom(state: EnquiryState): readonly Movable[] {
  return ENQUIRY_STATES.filter(
    (candidate): candidate is Movable =>
      candidate !== 'booked' && candidate !== 'lost' && candidate !== 'new' && candidate !== state,
  );
}

/**
 * WHETHER THE ROW BECAME A HOUSEHOLD: booked, or a booking cancelled before any clean, which is
 * `lost` carrying the conversion stamp (ops #47, step 4). Ending a household with no clean behind
 * her moves the enquiry she came from to `lost` and keeps the stamp, and the server refuses a person
 * every move out of that row and a second conversion of it, the same as a booked one. So both draw
 * the door to her household rather than the forms to work a lead.
 */
export function becameAHousehold(row: Pick<EnquiryRow, 'state' | 'convertedAt'>): boolean {
  return row.state === 'booked' || (row.state === 'lost' && row.convertedAt !== null);
}

/* ------------------------------------------------------------------ *
 * The groups, which are the server's own order named.
 * ------------------------------------------------------------------ */

/**
 * A transcription of the `urgency` ladder in `api/src/seams/enquiries.ts`, in the same order and
 * with the same tests, so the captions describe the order the rows actually arrived in. It decides
 * nothing: a row moves between groups only because the server sorted it there.
 */
function bucketOf(row: EnquiryRow, today: string): number {
  if (row.state === 'booked' || row.state === 'lost') return 4;
  if (row.state === 'new' && row.firstResponseAt === null) return 0;
  if (row.nextActionOn !== null && row.nextActionOn < today) return 1;
  if (row.ownerUserId === null) return 2;
  return 3;
}

const CAPTIONS: readonly string[] = [
  'Nobody has replied yet',
  'The next action has passed',
  'Nobody owns it',
  'Being worked',
  'Booked and lost',
];

export function bucketGroups(
  rows: readonly EnquiryRow[],
  today: string,
): readonly RowGroup<EnquiryRow>[] {
  return CAPTIONS.map((caption, index) => ({
    key: caption,
    caption,
    rows: rows.filter((row) => bucketOf(row, today) === index),
  })).filter((group) => group.rows.length > 0);
}

/* ------------------------------------------------------------------ *
 * Who has it, as a filter. Three answers and not one per person: this
 * office is three or four people, and "mine, nobody, somebody else"
 * is the question actually being asked at a desk. A filter per name
 * would be four selects to say what one already says.
 * ------------------------------------------------------------------ */

export const OWNER_FILTERS = ['mine', 'nobody', 'other'] as const;
export type OwnerFilter = (typeof OWNER_FILTERS)[number];

export const OWNER_FILTER_LABELS: Record<OwnerFilter, string> = Object.freeze({
  mine: 'Mine',
  nobody: 'Nobody',
  other: 'Somebody else',
});

function ownerRank(row: EnquiryRow, meId: string): number {
  if (row.ownerUserId === null) return 0;
  return row.ownerUserId === meId ? 1 : 2;
}

export function ownerMatches(row: EnquiryRow, want: OwnerFilter, meId: string): boolean {
  const rank = ownerRank(row, meId);
  if (want === 'nobody') return rank === 0;
  if (want === 'mine') return rank === 1;
  return rank === 2;
}

/* ------------------------------------------------------------------ *
 * The order the rows are in. Nothing here asks the server again.
 * ------------------------------------------------------------------ */

/**
 * Six of these are a column a finger presses. `arrived` is the seventh and it has no column: the
 * moment sits on the second line of the first cell, where the card puts it too, because a seventh
 * column would have come out of "Next action" and those are the coral words this screen is for.
 * It is chosen in the bar instead, which is the only order control a phone has anyway.
 */
export type SortKey = 'who' | 'town' | 'source' | 'arrived' | 'state' | 'owner' | 'next';

export interface Sorting {
  readonly key: SortKey;
  readonly direction: 'ascending' | 'descending';
}

/**
 * THE FOUR ORDERS SOMEBODY CHOOSES. `null` is the fifth thing the one piece of state can be: the
 * ladder the server sent, which `bucketGroups` names out loud, and which is what "what to work
 * next" means. Every named order is a sorting or that ladder, so the select and the column headers
 * write to the same place and the select can always say what the list is actually in.
 */
export const ORDERS = ['newest', 'oldest', 'next-action', 'name'] as const;
type NamedOrder = (typeof ORDERS)[number];

/** What the select says when a column header put the list in an order none of the four names. */
export type Order = NamedOrder | 'column';

export const ORDER_LABELS: Record<Order, string> = Object.freeze({
  newest: 'Newest first',
  oldest: 'Oldest first',
  'next-action': 'What to work next',
  name: 'By name',
  column: 'The column you pressed',
});

/** Each named order as a sorting, and `null` for the ladder the server sent. */
export const ORDER_SORTING: Record<NamedOrder, Sorting | null> = Object.freeze({
  newest: { key: 'arrived', direction: 'descending' },
  oldest: { key: 'arrived', direction: 'ascending' },
  'next-action': null,
  name: { key: 'who', direction: 'ascending' },
});

/** Which of the four the list is in, or `column` when a header asked for something else. */
export function orderOf(sorting: Sorting | null): Order {
  if (sorting === null) return 'next-action';
  for (const name of ORDERS) {
    const want = ORDER_SORTING[name];
    if (want && want.key === sorting.key && want.direction === sorting.direction) return name;
  }
  return 'column';
}

/**
 * The order somebody chose, remembered in this browser and nowhere else. It is a preference and
 * not a fact, so it never goes to the server and never joins the audit. Storage throws in a
 * private window and reads back empty in another browser, and both of those answers are the
 * default rather than a screen that will not draw.
 */
const ORDER_KEY = 'fk.enquiries.order';

export function rememberedOrder(): NamedOrder {
  try {
    const saved = window.localStorage.getItem(ORDER_KEY);
    return ORDERS.find((name) => name === saved) ?? 'newest';
  } catch {
    return 'newest';
  }
}

export function rememberOrder(order: NamedOrder): void {
  try {
    window.localStorage.setItem(ORDER_KEY, order);
  } catch {
    // A browser that refuses storage still sorts. It just forgets by the next visit.
  }
}

/** Nothing typed sorts after everything typed, so a blank never sits between two answers. */
function blankLast(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a.localeCompare(b);
}

function compareOn(key: SortKey, a: EnquiryRow, b: EnquiryRow, meId: string): number {
  switch (key) {
    case 'who':
      return a.name.localeCompare(b.name);
    case 'town':
      return blankLast(a.town, b.town);
    case 'source':
      return CHANNEL_LABELS[a.sourceChannel].localeCompare(CHANNEL_LABELS[b.sourceChannel]);
    case 'arrived':
      // Every instant the API returns is the same ISO UTC shape, so the strings compare exactly as
      // the moments do, with no Date and no zone anywhere in the comparison.
      return a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0;
    case 'state':
      return ENQUIRY_STATES.indexOf(a.state) - ENQUIRY_STATES.indexOf(b.state);
    case 'owner':
      return ownerRank(a, meId) - ownerRank(b, meId);
    case 'next':
      return blankLast(a.nextActionOn, b.nextActionOn) || blankLast(a.nextAction, b.nextAction);
  }
}

/** Stable: two rows the column cannot separate keep the order the server gave them. */
export function sortRows(
  rows: readonly EnquiryRow[],
  sorting: Sorting,
  meId: string,
): readonly EnquiryRow[] {
  const sign = sorting.direction === 'descending' ? -1 : 1;
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => sign * compareOn(sorting.key, a.row, b.row, meId) || a.index - b.index)
    .map((entry) => entry.row);
}
